import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToUserWallet,
  listenToUserSavedCards,
  cleanupWalletListeners,
} from "../../redux/transactionsSlice";
import {
  calculateTopUpFees,
  resetTopUpFees,
  getTopUpFeePercentage,
  calculateTopUpServiceFee,
} from "../../redux/walletServiceFeesSlice";
import countries, { CountryInfo } from "../../GLOBAL/Country";
import logo from "../../assets/cosmoola/logo.jpg";
import {
  ArrowLeft, CreditCard, Smartphone, Building2, User, CheckCircle,
  AlertCircle, Shield, Loader2, Eye, EyeOff, X, ChevronDown,
  ChevronRight, ArrowRight, RefreshCw, Search, Globe,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const D = {
  bg:"#000000", surface:"#0a0a0a", surface2:"#111111", border:"#1c1c1c",
  border2:"#2a2a2a", white:"#ffffff", offwhite:"#e8e8e8", muted:"#6b6b6b",
  subtle:"#3a3a3a", gold:"#C9A96E",
};
const L = {
  bg:"#ffffff", surface:"#fafafa", surface2:"#f3f3f3", border:"#e5e5e5",
  border2:"#d4d4d4", text:"#0a0a0a", textSoft:"#525252", muted:"#a3a3a3",
};

const API = "https://europe-west3-cosmoola.cloudfunctions.net";
const fmt = (n: number) => "€" + Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });
const QUICK_AMOUNTS = [5, 10, 25, 50, 100, 250, 500, 1000];

/* ─────────────────────────────────────────────────────────
   TRANSFER FEE (mirrors walletServiceFeesSlice tiers + transferFunctions.js)
   Used for: card, bank, mobile  (NOT cosmoola — zero fee)
───────────────────────────────────────────────────────── */
const calcTransferFee = (amountEUR: number): number => {
  if (amountEUR < 50)   return 1.50;
  if (amountEUR < 250)  return parseFloat((amountEUR * 0.01).toFixed(2));
  if (amountEUR < 1000) return parseFloat((amountEUR * 0.0075).toFixed(2));
  return parseFloat((amountEUR * 0.005).toFixed(2));
};
const feeLabel = (a: number) =>
  a < 50 ? "€1.50 flat" : a < 250 ? "1%" : a < 1000 ? "0.75%" : "0.5%";

/* ─────────────────────────────────────────────────────────
   MOBILE COUNTRIES
───────────────────────────────────────────────────────── */
type MobileProvider = { key:string; label:string; currency:string; currencySymbol:string; flag:string; minEUR:number; maxEUR:number };
type MobileEntry    = { countryCode:string; phoneCode:string; providers:MobileProvider[] };

const MOBILE_COUNTRIES: MobileEntry[] = [
  { countryCode:"KE", phoneCode:"+254", providers:[
    { key:"mpesa_ke",  label:"M-Pesa",       currency:"KES", currencySymbol:"KSh", flag:"🇰🇪", minEUR:1, maxEUR:1000 },
    { key:"airtel_ke", label:"Airtel Money",  currency:"KES", currencySymbol:"KSh", flag:"🇰🇪", minEUR:1, maxEUR:500  },
  ]},
  { countryCode:"TZ", phoneCode:"+255", providers:[
    { key:"mpesa_tz", label:"M-Pesa Tanzania",currency:"TZS", currencySymbol:"TSh", flag:"🇹🇿", minEUR:1, maxEUR:1500 },
    { key:"tigo_tz",  label:"Tigo Pesa",      currency:"TZS", currencySymbol:"TSh", flag:"🇹🇿", minEUR:1, maxEUR:1000 },
  ]},
  { countryCode:"UG", phoneCode:"+256", providers:[
    { key:"mpesa_ug",  label:"M-Pesa Uganda",  currency:"UGX", currencySymbol:"USh", flag:"🇺🇬", minEUR:1, maxEUR:1800 },
    { key:"mtn_ug",    label:"MTN Mobile Money",currency:"UGX", currencySymbol:"USh", flag:"🇺🇬", minEUR:1, maxEUR:1800 },
    { key:"airtel_ug", label:"Airtel Money",   currency:"UGX", currencySymbol:"USh", flag:"🇺🇬", minEUR:1, maxEUR:1200 },
  ]},
  { countryCode:"GH", phoneCode:"+233", providers:[
    { key:"mtn_gh",      label:"MTN MoMo",     currency:"GHS", currencySymbol:"GH₵", flag:"🇬🇭", minEUR:1, maxEUR:800 },
    { key:"vodafone_gh", label:"Vodafone Cash", currency:"GHS", currencySymbol:"GH₵", flag:"🇬🇭", minEUR:1, maxEUR:800 },
  ]},
  { countryCode:"NG", phoneCode:"+234", providers:[{ key:"momo_ng", label:"MoMo Nigeria", currency:"NGN", currencySymbol:"₦", flag:"🇳🇬", minEUR:1, maxEUR:600 }]},
  { countryCode:"SN", phoneCode:"+221", providers:[{ key:"orange_sn", label:"Orange Money", currency:"XOF", currencySymbol:"CFA", flag:"🇸🇳", minEUR:1, maxEUR:1000 }]},
  { countryCode:"ZW", phoneCode:"+263", providers:[{ key:"ecocash_zw", label:"EcoCash", currency:"ZWL", currencySymbol:"Z$", flag:"🇿🇼", minEUR:1, maxEUR:500 }]},
  { countryCode:"MZ", phoneCode:"+258", providers:[{ key:"mpesa_mz", label:"M-Pesa Mozambique", currency:"MZN", currencySymbol:"MT", flag:"🇲🇿", minEUR:1, maxEUR:600 }]},
  { countryCode:"PK", phoneCode:"+92",  providers:[{ key:"jazzcash_pk", label:"JazzCash", currency:"PKR", currencySymbol:"₨", flag:"🇵🇰", minEUR:1, maxEUR:1200 }]},
  { countryCode:"BD", phoneCode:"+880", providers:[{ key:"bkash_bd", label:"bKash", currency:"BDT", currencySymbol:"৳", flag:"🇧🇩", minEUR:1, maxEUR:2000 }]},
];
const MOBILE_CODES   = new Set(MOBILE_COUNTRIES.map(e => e.countryCode));
const getCountryInfo = (code: string): CountryInfo | undefined => countries.find(c => c.code === code);

/* ─────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────── */
type Method = "card" | "mobile" | "bank" | "cosmoola";
type Step   = "form" | "success" | "failed";

type CosmoolaUser = {
  userId: string; username: string | null;
  displayName: string; profilePicture: string | null;
};

/* ─────────────────────────────────────────────────────────
   USER AVATAR
───────────────────────────────────────────────────────── */
const Avatar: React.FC<{ user: CosmoolaUser; size?: number }> = ({ user, size = 40 }) => {
  const initials = user.displayName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  return user.profilePicture ? (
    <img src={user.profilePicture} alt={user.displayName}
      style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover", flexShrink:0 }}/>
  ) : (
    <div style={{ width:size, height:size, borderRadius:"50%", background:D.surface2, border:`1px solid ${D.border2}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
      <span style={{ fontSize:size*0.34, fontWeight:600, color:D.gold, fontFamily:"'Montserrat',sans-serif" }}>{initials}</span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const Transfer: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const { user }        = useSelector((s: RootState) => s.auth);
  const userId          = paramUserId ?? user?.userId ?? "";
  const { wallet, savedCards } = useSelector((s: RootState) => s.transactions);
  /* We use the slice for display only — actual fee tiers are calculated locally
     so the summary panel updates instantly without async round-trips */
  const { feePercentage: sliceFeePercent } = useSelector((s: RootState) => s.walletServiceFees);

  /* ── UI ── */
  const [loaded,        setLoaded]        = useState(false);
  const [balanceHidden, setBalanceHidden] = useState(false);
  const [method,        setMethod]        = useState<Method>("card");
  const [step,          setStep]          = useState<Step>("form");
  const [loading,       setLoading]       = useState(false);
  const [txnResult,     setTxnResult]     = useState<any>(null);
  const [errorMsg,      setErrorMsg]      = useState("");
  const [inputError,    setInputError]    = useState("");

  /* ── Amount ── */
  const [amount, setAmount] = useState(25);

  /* ── Card ── */
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);

  /* ── Mobile ── */
  const [mCode,        setMCode]        = useState("KE");
  const [mProviderKey, setMProviderKey] = useState("mpesa_ke");
  const [mPhone,       setMPhone]       = useState("");
  const [mDropOpen,    setMDropOpen]    = useState(false);
  const [mSearch,      setMSearch]      = useState("");
  const dropRef = useRef<HTMLDivElement>(null);

  /* ── Bank ── */
  const [bankType,    setBankType]    = useState<"sepa"|"swift">("sepa");
  const [bankHolder,  setBankHolder]  = useState("");
  const [bankIBAN,    setBankIBAN]    = useState("");
  const [bankBIC,     setBankBIC]     = useState("");
  const [bankName,    setBankName]    = useState("");
  const [bankRef,     setBankRef]     = useState("");
  const [corrBank,    setCorrBank]    = useState("");
  const [corrBIC,     setCorrBIC]     = useState("");

  /* ── Cosmoola user ── */
  const [cQuery,        setCQuery]        = useState("");
  const [cSearching,    setCSearching]    = useState(false);
  const [cResults,      setCResults]      = useState<CosmoolaUser[]>([]);
  const [cSelected,     setCSelected]     = useState<CosmoolaUser | null>(null);
  const [cSearchErr,    setCSearchErr]    = useState("");
  const [cNote,         setCNote]         = useState("");
  const cSearchTimer = useRef<NodeJS.Timeout | null>(null);

  /* ── Derived ── */
  const balance         = wallet?.balance ?? 0;
  const fee             = method === "cosmoola" ? 0 : calcTransferFee(amount);
  const net             = parseFloat((amount - fee).toFixed(2));
  const insufficient    = amount > balance;
  const mEntry          = MOBILE_COUNTRIES.find(e => e.countryCode === mCode)!;
  const mProvider       = mEntry?.providers.find(p => p.key === mProviderKey) ?? mEntry?.providers[0];
  const mCountryInfo    = getCountryInfo(mCode);
  const mPhoneCode      = mEntry?.phoneCode ?? "";
  const mobileList      = countries.filter(c => MOBILE_CODES.has(c.code));
  const filteredMobile  = mSearch ? mobileList.filter(c => c.name.toLowerCase().includes(mSearch.toLowerCase())) : mobileList;

  /* ── canTransfer ── */
  const canTransfer = (() => {
    if (amount < (method === "cosmoola" ? 0.01 : 5) || insufficient || loading) return false;
    if (method === "bank" && bankType === "swift" && amount < 100) return false;
    switch (method) {
      case "card":     return !!selectedCardId;
      case "mobile":   return mPhone.length >= 6 && !!mProvider;
      case "bank":     return bankHolder.trim().length > 1 && bankIBAN.trim().length > 5 && bankBIC.trim().length > 3;
      case "cosmoola": return !!cSelected;
      default:         return false;
    }
  })();

  /* ── Listeners ── */
  useEffect(() => {
    if (userId) { dispatch(listenToUserWallet(userId)); dispatch(listenToUserSavedCards(userId)); }
    return () => { dispatch(cleanupWalletListeners()); };
  }, [userId, dispatch]);

  useEffect(() => {
    if (savedCards.length && !selectedCardId) {
      const def = savedCards.find(c => c.isDefault);
      setSelectedCardId(def?.id ?? savedCards[0].id);
    }
  }, [savedCards, selectedCardId]);

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  /* Close mobile dropdown on outside click */
  useEffect(() => {
    const h = (e: MouseEvent) => { if (dropRef.current && !dropRef.current.contains(e.target as Node)) setMDropOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  /* ── Mobile country select ── */
  const selectMobileCountry = (code: string) => {
    setMCode(code);
    const entry = MOBILE_COUNTRIES.find(e => e.countryCode === code)!;
    setMProviderKey(entry.providers[0].key);
    setMDropOpen(false); setMSearch(""); setMPhone("");
  };

  /* ── Cosmoola user search (debounced 400ms) ── */
  const handleCSearch = (q: string) => {
    setCQuery(q); setCSelected(null); setCSearchErr(""); setCResults([]);
    if (cSearchTimer.current) clearTimeout(cSearchTimer.current);
    if (!q.trim() || q.trim().length < 2) return;
    cSearchTimer.current = setTimeout(async () => {
      setCSearching(true);
      try {
        const { data } = await axios.post(`${API}/lookupUser`, {
          query: q.trim().replace(/^@/, ""),
          searchBy: "username",
        });
        const filtered = (data.results ?? []).filter((u: CosmoolaUser) => u.userId !== userId);
        setCResults(filtered);
        if (filtered.length === 0) setCSearchErr("No Cosmoola user found");
      } catch (err: any) {
        setCSearchErr(err.response?.data?.error || "Search failed");
      } finally { setCSearching(false); }
    }, 400);
  };

  /* ── Validate ── */
  const validate = (): string => {
    if (method !== "cosmoola" && amount < 5)      return "Minimum transfer is €5.00";
    if (method === "cosmoola" && amount < 0.01)   return "Minimum is €0.01";
    if (method === "bank" && bankType === "swift" && amount < 100) return "Minimum SWIFT wire is €100.00";
    if (insufficient) return `Insufficient balance — you have ${fmt(balance)}`;
    if (method === "mobile" && mProvider && amount > mProvider.maxEUR)
      return `Max for ${mProvider.label} is €${mProvider.maxEUR.toLocaleString()}`;
    return "";
  };

  /* ── Submit ── */
  const handleTransfer = async () => {
    const err = validate();
    if (err) { setInputError(err); return; }
    if (!canTransfer) return;
    setInputError(""); setLoading(true); setErrorMsg("");

    try {
      let data: any;

      if (method === "card") {
        ({ data } = await axios.post(`${API}/transferToCard`, { userId, cardId: selectedCardId, amountEUR: amount }));
      }
      else if (method === "mobile") {
        const fullPhone = mPhoneCode + mPhone.replace(/^0/, "");
        ({ data } = await axios.post(`${API}/transferToMobileWallet`, {
          userId, provider: mProviderKey, phone: fullPhone, amountEUR: amount,
        }));
      }
      else if (method === "bank") {
        ({ data } = await axios.post(`${API}/transferToBank`, {
          userId, amountEUR: amount, transferType: bankType,
          bankDetails: {
            accountHolder: bankHolder, iban: bankIBAN, bic: bankBIC,
            bankName: bankName || undefined,
            referenceNote: bankRef || undefined,
            correspondentBank: corrBank || undefined,
            correspondentBIC:  corrBIC  || undefined,
          },
        }));
      }
      else if (method === "cosmoola") {
        ({ data } = await axios.post(`${API}/sendToCosmoolaUser`, {
          senderId: userId, query: cSelected!.userId, searchBy: "userId",
          amountEUR: amount, note: cNote.trim(),
        }));
      }

      setTxnResult({ ...data, method, mProvider, mPhoneCode, mPhone });
      setStep("success");
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || err.message || "Transfer failed");
      setStep("failed");
    } finally { setLoading(false); }
  };

  const reset = () => {
    setStep("form"); setTxnResult(null); setErrorMsg(""); setInputError("");
    setAmount(25); setMPhone(""); setCSelected(null); setCQuery(""); setCResults([]); setCNote("");
    setBankHolder(""); setBankIBAN(""); setBankBIC(""); setBankName(""); setBankRef(""); setCorrBank(""); setCorrBIC("");
  };

  /* ─── RENDER HELPERS ─── */
  const inputStyle = (focus?: boolean): React.CSSProperties => ({
    width:"100%", background:L.surface2, border:`1px solid ${focus ? L.text : L.border}`,
    padding:"12px 14px", color:L.text, fontSize:13,
    fontFamily:"'Montserrat',sans-serif", outline:"none",
  });

  const labelStyle: React.CSSProperties = {
    fontSize:9, color:L.muted, letterSpacing:"0.2em", display:"block", marginBottom:7,
  };

  const METHOD_TABS: { id:Method; label:string; icon:React.ReactNode; sub:string }[] = [
    { id:"card",     label:"Saved Card",    icon:<CreditCard size={16} strokeWidth={1.5}/>,  sub:"1–3 business days" },
    { id:"mobile",   label:"Mobile Wallet", icon:<Smartphone size={16} strokeWidth={1.5}/>,  sub:"M-Pesa · Airtel · MTN" },
    { id:"bank",     label:"Bank Transfer", icon:<Building2 size={16} strokeWidth={1.5}/>,   sub:"SEPA & SWIFT" },
    { id:"cosmoola", label:"Cosmoola User", icon:<User size={16} strokeWidth={1.5}/>,        sub:"Instant · Zero fee" },
  ];

  return (
    <div style={{ minHeight:"100vh", background:L.bg, color:L.text, fontFamily:"'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{TR_CSS}</style>

      {/* HEADER */}
      <header style={{ position:"sticky", top:0, zIndex:100, background:"rgba(0,0,0,0.96)", backdropFilter:"blur(14px)", borderBottom:`1px solid ${D.border}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 20px", height:58 }}>
        <button onClick={() => navigate(-1)} style={{ background:"none", border:"none", cursor:"pointer", color:D.offwhite, display:"flex", alignItems:"center", gap:6, fontSize:11, fontFamily:"'Montserrat',sans-serif", letterSpacing:"0.05em" }}>
          <ArrowLeft size={16} strokeWidth={1.5}/><span className="tr-back-label">BACK</span>
        </button>
        <div style={{ position:"absolute", left:"50%", transform:"translateX(-50%)" }}>
          <span style={{ color:D.white, fontWeight:300, letterSpacing:"0.35em", fontFamily:"'Cormorant Garamond',serif", fontSize:15 }}>COSMOOLA</span>
        </div>
        <img src={logo} alt="" style={{ width:28, height:28, objectFit:"cover" }}/>
      </header>

      <div className="tr-container">
        <div className="tr-main">

          {/* TITLE */}
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4 }} style={{ marginBottom:28 }}>
            <h1 style={{ fontSize:28, fontWeight:300, color:L.text, fontFamily:"'Cormorant Garamond',serif", letterSpacing:"0.06em", margin:0 }}>Transfer</h1>
            <p style={{ fontSize:12, color:L.textSoft, marginTop:6, letterSpacing:"0.05em" }}>Send money from your Cosmoola wallet</p>
          </motion.div>

          <AnimatePresence mode="wait">

            {/* ── SUCCESS ── */}
            {step === "success" && txnResult && (
              <motion.div key="success" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-12 }}
                style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"40px 28px", textAlign:"center", marginBottom:20 }}>
                <div style={{ width:60, height:60, borderRadius:"50%", background:"#f0f9f0", border:"1px solid #d4ecd4", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
                  <CheckCircle size={28} color="#2d7a2d"/>
                </div>
                <h2 style={{ fontSize:22, fontWeight:300, color:L.text, fontFamily:"'Cormorant Garamond',serif", marginBottom:8 }}>
                  {method === "cosmoola" ? "Sent!" : "Transfer Initiated"}
                </h2>

                {/* Recipient preview for Cosmoola transfer */}
                {method === "cosmoola" && cSelected && (
                  <div style={{ display:"flex", alignItems:"center", gap:12, justifyContent:"center", marginBottom:16 }}>
                    <Avatar user={cSelected} size={44}/>
                    <div style={{ textAlign:"left" }}>
                      <div style={{ fontSize:14, fontWeight:500, color:L.text }}>{cSelected.displayName}</div>
                      {cSelected.username && <div style={{ fontSize:10, color:L.muted }}>@{cSelected.username}</div>}
                    </div>
                  </div>
                )}

                <p style={{ fontSize:12, color:L.textSoft, lineHeight:1.8, marginBottom:20 }}>
                  {method === "card" && <><strong>{fmt(txnResult.netPayoutEUR)}</strong> to <strong>{txnResult.cardBrand?.toUpperCase()} ••••{txnResult.cardLast4}</strong>. Arrives in 1–3 business days.</>}
                  {method === "mobile" && <><strong>{txnResult.netLocalAmount?.toLocaleString()} {txnResult.localCurrency}</strong> to <strong>{txnResult.phone}</strong> via <strong>{txnResult.providerLabel}</strong>. Typically 1–24 hours.</>}
                  {method === "bank" && <><strong>{fmt(txnResult.netPayoutEUR)}</strong> bank transfer processing. Arrives in <strong>{txnResult.estimatedArrival}</strong>.</>}
                  {method === "cosmoola" && <><strong>{fmt(amount)}</strong> sent to <strong>{cSelected?.displayName}</strong> instantly. No fees.</>}
                </p>

                {/* Summary */}
                <div style={{ background:D.bg, border:`1px solid ${D.border}`, padding:"16px 20px", textAlign:"left", marginBottom:24 }}>
                  {[
                    { label:"Amount",    value:fmt(amount) },
                    { label:"Fee",       value:method==="cosmoola" ? "€0.00 (zero fee)" : `− ${fmt(fee)} (${feeLabel(amount)})` },
                    { label:"Net payout",value:method==="cosmoola" ? fmt(amount) : fmt(net) },
                    ...(method==="mobile"&&txnResult.fxRate ? [{ label:"FX rate", value:`1 EUR = ${txnResult.fxRate?.toFixed(4)} ${txnResult.localCurrency}` }] : []),
                  ].map(r => (
                    <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:`1px solid ${D.border}` }}>
                      <span style={{ fontSize:11, color:D.muted }}>{r.label}</span>
                      <span style={{ fontSize:11, color:D.offwhite, fontWeight:500 }}>{r.value}</span>
                    </div>
                  ))}
                </div>

                <div style={{ display:"flex", gap:10 }}>
                  <motion.button whileTap={{ scale:0.97 } as any} onClick={reset}
                    style={{ flex:1, padding:"13px 0", background:"transparent", border:`1px solid ${L.border}`, color:L.textSoft, cursor:"pointer", fontSize:11, fontWeight:500, letterSpacing:"0.14em", fontFamily:"'Montserrat',sans-serif" }}>
                    NEW TRANSFER
                  </motion.button>
                  <motion.button whileTap={{ scale:0.97 } as any} onClick={() => navigate(`/transactions/${userId}`)}
                    style={{ flex:1, padding:"13px 0", background:D.bg, border:"none", color:D.white, cursor:"pointer", fontSize:11, fontWeight:600, letterSpacing:"0.14em", fontFamily:"'Montserrat',sans-serif" }}>
                    VIEW HISTORY
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* ── FAILED ── */}
            {step === "failed" && (
              <motion.div key="failed" initial={{ opacity:0, y:12 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-12 }}
                style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"40px 28px", textAlign:"center", marginBottom:20 }}>
                <div style={{ width:60, height:60, borderRadius:"50%", background:"#fff0f0", border:"1px solid #f0c8c8", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
                  <AlertCircle size={28} color="#B22222"/>
                </div>
                <h2 style={{ fontSize:22, fontWeight:300, color:L.text, fontFamily:"'Cormorant Garamond',serif", marginBottom:8 }}>Transfer Failed</h2>
                <p style={{ fontSize:12, color:L.textSoft, lineHeight:1.8, marginBottom:24, maxWidth:340, margin:"0 auto 24px" }}>{errorMsg}</p>
                <motion.button whileTap={{ scale:0.97 } as any} onClick={reset}
                  style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"12px 28px", background:D.bg, border:"none", color:D.white, cursor:"pointer", fontSize:11, fontWeight:600, letterSpacing:"0.16em", fontFamily:"'Montserrat',sans-serif" }}>
                  <RefreshCw size={13}/> TRY AGAIN
                </motion.button>
              </motion.div>
            )}

            {/* ── FORM ── */}
            {step === "form" && (
              <motion.div key="form" initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }}>

                {/* BALANCE CARD */}
                <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4, delay:0.04 }}
                  style={{ background:D.bg, border:`1px solid ${D.border2}`, padding:"24px 22px", marginBottom:20 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div>
                      <p style={{ fontSize:9, color:D.muted, letterSpacing:"0.25em", marginBottom:8 }}>AVAILABLE BALANCE</p>
                      <p style={{ fontSize:32, fontWeight:300, color:D.white, fontFamily:"'Cormorant Garamond',serif", letterSpacing:"-0.02em" }}>
                        {balanceHidden ? "€ •••••" : fmt(balance)}
                      </p>
                    </div>
                    <button onClick={() => setBalanceHidden(v => !v)} style={{ background:"none", border:"none", cursor:"pointer", padding:4 }}>
                      {balanceHidden ? <Eye size={16} color={D.muted}/> : <EyeOff size={16} color={D.muted}/>}
                    </button>
                  </div>
                  {insufficient && amount >= (method==="cosmoola"?0.01:5) && (
                    <div style={{ marginTop:12, padding:"8px 12px", background:"rgba(180,30,30,0.08)", border:"1px solid rgba(180,30,30,0.2)", display:"flex", alignItems:"center", gap:8 }}>
                      <AlertCircle size={12} color="#cc4444"/>
                      <span style={{ fontSize:10, color:"#cc4444" }}>Insufficient balance for this transfer amount</span>
                    </div>
                  )}
                  <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${D.border}`, display:"flex", alignItems:"center", gap:8 }}>
                    <Shield size={12} color={D.gold}/>
                    <span style={{ fontSize:10, color:D.muted, letterSpacing:"0.1em" }}>TRANSFERS SECURED BY COSMOOLA</span>
                  </div>
                </motion.div>

                {/* METHOD TABS */}
                <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4, delay:0.08 }} style={{ marginBottom:20 }}>
                  <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:12 }}>TRANSFER TO</p>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:8 }}>
                    {METHOD_TABS.map(m => (
                      <motion.button key={m.id} whileTap={{ scale:0.97 } as any} onClick={() => { setMethod(m.id); setInputError(""); }}
                        style={{
                          padding:"14px 8px", textAlign:"center",
                          background: method===m.id ? L.text : "transparent",
                          border: method===m.id ? `2px solid ${L.text}` : `1px solid ${L.border}`,
                          cursor:"pointer", transition:"all 0.18s",
                        }}>
                        <div style={{ display:"flex", justifyContent:"center", marginBottom:6, color:method===m.id?L.bg:L.muted }}>{m.icon}</div>
                        <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.12em", fontFamily:"'Montserrat',sans-serif", color:method===m.id?L.bg:L.text }}>{m.label.toUpperCase()}</div>
                        <div style={{ fontSize:8, color:method===m.id?"rgba(255,255,255,0.55)":L.muted, marginTop:3, lineHeight:1.4 }}>{m.sub}</div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>

                {/* AMOUNT */}
                <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4, delay:0.12 }}
                  style={{ background:L.surface, border:`1px solid ${inputError||insufficient?L.border2:L.border}`, padding:"26px 22px", marginBottom:20 }}>
                  <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:18 }}>AMOUNT (EUR)</p>
                  <div style={{ textAlign:"center", marginBottom:20 }}>
                    <div style={{ fontSize:52, fontWeight:300, color:insufficient?"#cc4444":L.text, fontFamily:"'Cormorant Garamond',serif", letterSpacing:"-0.02em", transition:"color 0.2s" }}>
                      €{amount.toLocaleString("de-DE")}
                    </div>
                    {method === "cosmoola" && (
                      <div style={{ fontSize:10, color:"#2d7a2d", marginTop:6, letterSpacing:"0.1em", fontWeight:600 }}>ZERO FEE · INSTANT</div>
                    )}
                  </div>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:8, justifyContent:"center", marginBottom:14 }}>
                    {QUICK_AMOUNTS.map(q => (
                      <motion.button key={q} whileTap={{ scale:0.95 } as any} onClick={() => { setAmount(q); setInputError(""); }}
                        style={{ padding:"8px 14px", border:amount===q?`1px solid ${L.text}`:`1px solid ${L.border}`, background:amount===q?L.text:"transparent", color:amount===q?L.bg:L.textSoft, cursor:"pointer", fontSize:11, fontWeight:500, fontFamily:"'Montserrat',sans-serif" }}>
                        €{q.toLocaleString()}
                      </motion.button>
                    ))}
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:11, color:L.muted, flexShrink:0 }}>Custom (€):</span>
                    <input type="number" min={method==="cosmoola"?0.01:5} max={500000}
                      value={amount}
                      onChange={e => { const v=Number(e.target.value); if (!isNaN(v)) { setAmount(Math.max(0, Math.min(500000,v))); setInputError(""); }}}
                      style={{ flex:1, background:L.surface2, border:`1px solid ${L.border}`, padding:"10px 14px", color:L.text, fontSize:14, fontFamily:"'Montserrat',sans-serif", outline:"none", textAlign:"right" }}
                    />
                  </div>
                  {inputError && (
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:10, padding:"8px 12px", background:"#fff0f0", border:"1px solid #f0c8c8" }}>
                      <AlertCircle size={12} color="#B22222"/>
                      <span style={{ fontSize:11, color:"#B22222" }}>{inputError}</span>
                    </div>
                  )}
                </motion.div>

                {/* ─── DESTINATION PANELS ─── */}
                <AnimatePresence mode="wait">

                  {/* CARD */}
                  {method === "card" && (
                    <motion.div key="card" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }} transition={{ duration:0.2 }} style={{ marginBottom:20 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                        <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em" }}>DESTINATION CARD</p>
                        <button onClick={() => navigate(`/PaymentMethods/${userId}`)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:L.muted, fontFamily:"inherit" }}>Manage →</button>
                      </div>
                      {savedCards.length === 0 ? (
                        <div style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"28px 22px", textAlign:"center" }}>
                          <CreditCard size={26} color={L.muted} style={{ marginBottom:12 }}/>
                          <p style={{ fontSize:13, color:L.textSoft, marginBottom:14 }}>No saved cards</p>
                          <button onClick={() => navigate(`/PaymentMethods/${userId}`)}
                            style={{ background:L.text, color:L.bg, border:"none", padding:"10px 24px", cursor:"pointer", fontSize:11, fontWeight:500, letterSpacing:"0.14em", fontFamily:"'Montserrat',sans-serif" }}>
                            ADD A CARD
                          </button>
                        </div>
                      ) : (
                        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                          {savedCards.map(card => (
                            <motion.button key={card.id} whileTap={{ scale:0.98 } as any} onClick={() => setSelectedCardId(card.id)}
                              style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 18px", background:selectedCardId===card.id?L.surface:"transparent", border:selectedCardId===card.id?`2px solid ${L.text}`:`1px solid ${L.border}`, cursor:"pointer", textAlign:"left", width:"100%", fontFamily:"inherit", transition:"all 0.18s" }}>
                              <div style={{ width:20, height:20, borderRadius:"50%", flexShrink:0, border:selectedCardId===card.id?`6px solid ${L.text}`:`2px solid ${L.border}`, background:L.bg, transition:"all 0.15s" }}/>
                              <div style={{ width:42, height:28, borderRadius:4, flexShrink:0, background:D.bg, border:`1px solid ${D.border2}`, display:"flex", alignItems:"center", justifyContent:"center" }}>
                                <CreditCard size={14} color={D.offwhite}/>
                              </div>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:13, fontWeight:500, color:L.text }}>{card.brand.toUpperCase()} •••• {card.last4}</div>
                                <div style={{ fontSize:10, color:L.muted, marginTop:2 }}>
                                  Expires {String(card.expMonth).padStart(2,"0")}/{card.expYear}
                                  {card.isDefault && <span style={{ marginLeft:8, color:L.text, fontWeight:600 }}>DEFAULT</span>}
                                </div>
                              </div>
                              {selectedCardId===card.id && <CheckCircle size={15} color={L.text}/>}
                            </motion.button>
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* MOBILE */}
                  {method === "mobile" && (
                    <motion.div key="mobile" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }} transition={{ duration:0.2 }} style={{ marginBottom:20 }}>
                      <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:12 }}>DESTINATION</p>

                      {/* Country dropdown */}
                      <div style={{ marginBottom:14 }} ref={dropRef}>
                        <label style={labelStyle}>COUNTRY</label>
                        <div onClick={() => setMDropOpen(v=>!v)}
                          style={{ display:"flex", alignItems:"center", justifyContent:"space-between", background:L.surface2, border:`1px solid ${mDropOpen?L.text:L.border}`, padding:"13px 16px", cursor:"pointer", transition:"border-color 0.2s" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontSize:18 }}>{mCountryInfo?.emoji ?? "🌍"}</span>
                            <span style={{ fontSize:13, fontWeight:500, color:L.text }}>{mCountryInfo?.name ?? mCode}</span>
                            <span style={{ fontSize:10, color:L.muted }}>{mPhoneCode}</span>
                          </div>
                          <ChevronDown size={14} color={L.muted} style={{ transform:mDropOpen?"rotate(180deg)":"none", transition:"transform 0.2s" }}/>
                        </div>
                        <AnimatePresence>
                          {mDropOpen && (
                            <motion.div initial={{ opacity:0, height:0 }} animate={{ opacity:1, height:"auto" }} exit={{ opacity:0, height:0 }} transition={{ duration:0.2 }}
                              style={{ overflow:"hidden", border:`1px solid ${L.border}`, borderTop:"none", background:L.bg, position:"relative", zIndex:50 }}>
                              <div style={{ padding:"10px 12px", borderBottom:`1px solid ${L.border}` }}>
                                <input autoFocus placeholder="Search countries..." value={mSearch} onChange={e => setMSearch(e.target.value)}
                                  style={{ width:"100%", background:L.surface2, border:`1px solid ${L.border}`, padding:"9px 12px", fontSize:12, color:L.text, outline:"none", fontFamily:"'Montserrat',sans-serif" }}/>
                              </div>
                              <div style={{ maxHeight:220, overflowY:"auto" }}>
                                {filteredMobile.map(c => (
                                  <div key={c.code} onClick={() => selectMobileCountry(c.code)}
                                    style={{ display:"flex", alignItems:"center", gap:10, padding:"12px 16px", cursor:"pointer", background:mCode===c.code?L.surface:"transparent", borderBottom:`1px solid ${L.border}` }}>
                                    <span style={{ fontSize:18 }}>{c.emoji}</span>
                                    <div style={{ flex:1 }}>
                                      <div style={{ fontSize:13, fontWeight:500, color:L.text }}>{c.name}</div>
                                      <div style={{ fontSize:9, color:L.muted, marginTop:2 }}>
                                        {MOBILE_COUNTRIES.find(e=>e.countryCode===c.code)?.providers.map(p=>p.label).join(" · ")}
                                      </div>
                                    </div>
                                    <span style={{ fontSize:10, color:L.muted }}>{c.phoneCode}</span>
                                  </div>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {/* Provider */}
                      {mEntry?.providers.length > 1 && (
                        <div style={{ marginBottom:14 }}>
                          <label style={labelStyle}>PROVIDER</label>
                          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                            {mEntry.providers.map(p => (
                              <button key={p.key} onClick={() => setMProviderKey(p.key)}
                                style={{ padding:"8px 16px", border:mProviderKey===p.key?`2px solid ${L.text}`:`1px solid ${L.border}`, background:mProviderKey===p.key?L.text:"transparent", color:mProviderKey===p.key?L.bg:L.text, cursor:"pointer", fontSize:11, fontWeight:500, fontFamily:"'Montserrat',sans-serif" }}>
                                {p.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Phone */}
                      <div style={{ marginBottom:14 }}>
                        <label style={labelStyle}>PHONE NUMBER</label>
                        <div style={{ display:"flex" }}>
                          <div style={{ display:"flex", alignItems:"center", gap:6, padding:"0 14px", background:L.surface2, border:`1px solid ${L.border}`, borderRight:"none", fontSize:13, color:L.textSoft, flexShrink:0 }}>
                            <span style={{ fontSize:16 }}>{mCountryInfo?.emoji}</span>
                            <span style={{ fontWeight:500 }}>{mPhoneCode}</span>
                          </div>
                          <input type="tel" placeholder="712 345 678" value={mPhone}
                            onChange={e => setMPhone(e.target.value.replace(/[^0-9]/g,""))}
                            style={{ flex:1, background:L.surface2, border:`1px solid ${L.border}`, padding:"13px 14px", color:L.text, fontSize:14, fontFamily:"'Montserrat',sans-serif", outline:"none" }}/>
                        </div>
                        <p style={{ fontSize:9, color:L.muted, marginTop:5 }}>Enter without country code — {mPhoneCode} added automatically</p>
                      </div>

                      {mProvider && (
                        <div style={{ padding:"10px 14px", background:"#fffbf0", border:"1px solid #f0e8c8", display:"flex", alignItems:"center", gap:8 }}>
                          <Smartphone size={12} color="#b8860b"/>
                          <span style={{ fontSize:10, color:"#b8860b" }}>{mProvider.label}: €{mProvider.minEUR}–€{mProvider.maxEUR.toLocaleString()} per transfer</span>
                        </div>
                      )}
                    </motion.div>
                  )}

                  {/* BANK */}
                  {method === "bank" && (
                    <motion.div key="bank" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }} transition={{ duration:0.2 }} style={{ marginBottom:20 }}>
                      <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:12 }}>BANK DETAILS</p>

                      {/* SEPA / SWIFT toggle */}
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:18 }}>
                        {(["sepa","swift"] as const).map(t => (
                          <motion.button key={t} whileTap={{ scale:0.97 } as any} onClick={() => setBankType(t)}
                            style={{ padding:"14px 12px", textAlign:"center", background:bankType===t?L.text:"transparent", border:bankType===t?`2px solid ${L.text}`:`1px solid ${L.border}`, cursor:"pointer", transition:"all 0.18s" }}>
                            <div style={{ display:"flex", justifyContent:"center", marginBottom:6, color:bankType===t?L.bg:L.muted }}>
                              {t === "sepa" ? <Building2 size={18}/> : <Globe size={18}/>}
                            </div>
                            <div style={{ fontSize:11, fontWeight:700, letterSpacing:"0.14em", fontFamily:"'Montserrat',sans-serif", color:bankType===t?L.bg:L.text }}>{t.toUpperCase()}</div>
                            <div style={{ fontSize:9, color:bankType===t?"rgba(255,255,255,0.5)":L.muted, marginTop:3 }}>
                              {t==="sepa"?"EU/EEA · 1–2 days":"Worldwide · 3–5 days"}
                            </div>
                          </motion.button>
                        ))}
                      </div>

                      {bankType === "swift" && amount < 100 && (
                        <div style={{ padding:"10px 14px", background:"#fff0f0", border:"1px solid #f0c8c8", marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
                          <AlertCircle size={12} color="#B22222"/>
                          <span style={{ fontSize:10, color:"#B22222" }}>Minimum SWIFT wire is €100.00</span>
                        </div>
                      )}

                      {/* Bank fields */}
                      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                        <div>
                          <label style={labelStyle}>ACCOUNT HOLDER NAME *</label>
                          <input placeholder="Full name on account" value={bankHolder} onChange={e => setBankHolder(e.target.value)} style={inputStyle()}/>
                        </div>
                        <div>
                          <label style={labelStyle}>IBAN *</label>
                          <input placeholder="EE38 2200 2210 2014 5685" value={bankIBAN} onChange={e => setBankIBAN(e.target.value.toUpperCase())} style={{ ...inputStyle(), fontFamily:"'Courier New',monospace" }}/>
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                          <div>
                            <label style={labelStyle}>BIC / SWIFT *</label>
                            <input placeholder="HABAEE2X" value={bankBIC} onChange={e => setBankBIC(e.target.value.toUpperCase())} style={{ ...inputStyle(), fontFamily:"'Courier New',monospace" }}/>
                          </div>
                          <div>
                            <label style={labelStyle}>BANK NAME</label>
                            <input placeholder="Optional" value={bankName} onChange={e => setBankName(e.target.value)} style={inputStyle()}/>
                          </div>
                        </div>
                        <div>
                          <label style={labelStyle}>PAYMENT REFERENCE / NOTE</label>
                          <input placeholder="Optional — invoice no., purpose, etc." maxLength={140} value={bankRef} onChange={e => setBankRef(e.target.value)} style={inputStyle()}/>
                        </div>

                        {/* SWIFT extras */}
                        {bankType === "swift" && (
                          <>
                            <div style={{ paddingTop:8, borderTop:`1px solid ${L.border}` }}>
                              <p style={{ fontSize:9, color:L.muted, letterSpacing:"0.2em", marginBottom:10 }}>CORRESPONDENT BANK (USD / GBP WIRES)</p>
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                              <div>
                                <label style={labelStyle}>CORRESPONDENT BANK</label>
                                <input placeholder="e.g. Deutsche Bank AG" value={corrBank} onChange={e => setCorrBank(e.target.value)} style={inputStyle()}/>
                              </div>
                              <div>
                                <label style={labelStyle}>CORRESPONDENT BIC</label>
                                <input placeholder="e.g. DEUTDEDB" value={corrBIC} onChange={e => setCorrBIC(e.target.value.toUpperCase())} style={{ ...inputStyle(), fontFamily:"'Courier New',monospace" }}/>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      <div style={{ marginTop:14, padding:"10px 14px", background:"#f0f9f0", border:"1px solid #d4ecd4", display:"flex", alignItems:"center", gap:8 }}>
                        <Shield size={12} color="#2d7a2d"/>
                        <span style={{ fontSize:10, color:"#2d7a2d" }}>Bank details are encrypted and processed securely by Cosmoola's banking partner</span>
                      </div>
                    </motion.div>
                  )}

                  {/* COSMOOLA USER */}
                  {method === "cosmoola" && (
                    <motion.div key="cosmoola" initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }} transition={{ duration:0.2 }} style={{ marginBottom:20 }}>
                      <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:12 }}>SEND TO</p>

                      {/* Search */}
                      <div style={{ position:"relative", marginBottom:14 }}>
                        <label style={labelStyle}>SEARCH BY USERNAME</label>
                        <div style={{ display:"flex", alignItems:"center", background:L.surface2, border:`1px solid ${cQuery?L.text:L.border}`, transition:"border-color 0.2s" }}>
                          <div style={{ padding:"0 14px", color:L.muted, display:"flex", alignItems:"center" }}>
                            {cSearching ? <Loader2 size={15} className="tr-spin"/> : <Search size={15}/>}
                          </div>
                          <input type="text" placeholder="@username" value={cQuery}
                            onChange={e => handleCSearch(e.target.value)}
                            style={{ flex:1, background:"transparent", border:"none", padding:"13px 14px 13px 0", fontSize:14, color:L.text, outline:"none", fontFamily:"'Montserrat',sans-serif" }}/>
                          {cQuery && (
                            <button onClick={() => { setCQuery(""); setCResults([]); setCSearchErr(""); setCSelected(null); }}
                              style={{ background:"none", border:"none", cursor:"pointer", padding:"0 14px", color:L.muted }}><X size={13}/></button>
                          )}
                        </div>

                        {/* Dropdown */}
                        <AnimatePresence>
                          {cResults.length > 0 && !cSelected && (
                            <motion.div initial={{ opacity:0, y:-4 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}
                              style={{ position:"absolute", top:"100%", left:0, right:0, background:L.bg, border:`1px solid ${L.border}`, borderTop:"none", zIndex:40, maxHeight:220, overflowY:"auto" }}>
                              {cResults.map(u => (
                                <div key={u.userId} onClick={() => { setCSelected(u); setCQuery(u.displayName); setCResults([]); }}
                                  style={{ display:"flex", alignItems:"center", gap:12, padding:"13px 16px", cursor:"pointer", borderBottom:`1px solid ${L.border}` }}
                                  onMouseEnter={e => (e.currentTarget.style.background=L.surface)}
                                  onMouseLeave={e => (e.currentTarget.style.background="transparent")}>
                                  <Avatar user={u} size={36}/>
                                  <div>
                                    <div style={{ fontSize:13, fontWeight:500, color:L.text }}>{u.displayName}</div>
                                    {u.username && <div style={{ fontSize:10, color:L.muted, marginTop:2 }}>@{u.username}</div>}
                                  </div>
                                  <ChevronRight size={13} color={L.muted} style={{ marginLeft:"auto" }}/>
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                        {cSearchErr && !cSelected && cQuery.length>=2 && (
                          <p style={{ fontSize:11, color:L.muted, marginTop:8 }}>{cSearchErr}</p>
                        )}
                      </div>

                      {/* Selected user card */}
                      <AnimatePresence>
                        {cSelected && (
                          <motion.div initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }}>
                            <div style={{ display:"flex", alignItems:"center", gap:14, padding:"16px 18px", background:L.surface2, border:`1px solid ${L.border}`, marginBottom:14 }}>
                              <Avatar user={cSelected} size={46}/>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:15, fontWeight:500, color:L.text }}>{cSelected.displayName}</div>
                                {cSelected.username && <div style={{ fontSize:11, color:L.muted, marginTop:3 }}>@{cSelected.username}</div>}
                              </div>
                              <button onClick={() => { setCSelected(null); setCQuery(""); }}
                                style={{ background:"none", border:"none", cursor:"pointer", color:L.muted }}><X size={14}/></button>
                            </div>

                            {/* Note */}
                            <div>
                              <label style={labelStyle}>NOTE (OPTIONAL)</label>
                              <input type="text" maxLength={280} placeholder="What's this for?" value={cNote}
                                onChange={e => setCNote(e.target.value)} style={inputStyle()}/>
                            </div>

                            {/* Zero-fee badge */}
                            <div style={{ marginTop:12, padding:"10px 14px", background:"#f0f9f0", border:"1px solid #d4ecd4", display:"flex", alignItems:"center", gap:8 }}>
                              <CheckCircle size={12} color="#2d7a2d"/>
                              <span style={{ fontSize:10, color:"#2d7a2d", fontWeight:600 }}>Zero fee · Arrives instantly in {cSelected.displayName}'s wallet</span>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  )}

                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* ── RIGHT SUMMARY ── */}
        <div className="tr-summary">
          <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:loaded?1:0, y:loaded?0:16 }} transition={{ duration:0.4, delay:0.25 }}
            style={{ background:L.surface, border:`1px solid ${L.border}`, padding:"28px 22px", position:"sticky", top:80 }}>

            {step === "form" && (<>
              <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:20 }}>TRANSFER SUMMARY</p>

              <div style={{ display:"flex", flexDirection:"column", gap:14, marginBottom:20 }}>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:13, color:L.textSoft }}>Amount</span>
                  <span style={{ fontSize:13, fontWeight:500, color:L.text }}>{fmt(amount)}</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:13, color:L.textSoft }}>
                    Fee <span style={{ fontSize:10, color:L.muted, marginLeft:4 }}>({method==="cosmoola"?"none":feeLabel(amount)})</span>
                  </span>
                  <span style={{ fontSize:13, color: method==="cosmoola"?"#2d7a2d":L.textSoft }}>
                    {method==="cosmoola"?"€0.00 ✓":`− ${fmt(fee)}`}
                  </span>
                </div>
                <div style={{ height:1, background:L.border }}/>
                <div style={{ display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:14, fontWeight:600, color:L.text }}>
                    {method==="cosmoola"?"They receive":"Net payout"}
                  </span>
                  <span style={{ fontSize:20, fontWeight:300, color:insufficient?"#cc4444":L.text, fontFamily:"'Cormorant Garamond',serif", transition:"color 0.2s" }}>
                    {method==="cosmoola" ? fmt(amount) : fmt(net)}
                  </span>
                </div>
              </div>

              {/* Destination preview */}
              {method==="card" && selectedCardId && (() => {
                const card = savedCards.find(c => c.id===selectedCardId);
                if (!card) return null;
                return (
                  <div style={{ background:D.bg, border:`1px solid ${D.border2}`, padding:"13px 15px", display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                    <CreditCard size={14} color={D.offwhite}/>
                    <div><div style={{ fontSize:11, color:D.offwhite, fontWeight:500 }}>{card.brand.toUpperCase()} •••• {card.last4}</div>
                      <div style={{ fontSize:9, color:D.muted, marginTop:2 }}>Receiving {fmt(net)}</div></div>
                  </div>
                );
              })()}

              {method==="mobile" && mPhone.length>=6 && mProvider && (
                <div style={{ background:D.bg, border:`1px solid ${D.border2}`, padding:"13px 15px", display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                  <Smartphone size={14} color={D.offwhite}/>
                  <div><div style={{ fontSize:11, color:D.offwhite, fontWeight:500 }}>{mProvider.label} · {mPhoneCode}{mPhone}</div>
                    <div style={{ fontSize:9, color:D.muted, marginTop:2 }}>~est. {mProvider.currencySymbol}{Math.round(net * (1/0.007)).toLocaleString()} {mProvider.currency}</div></div>
                </div>
              )}

              {method==="bank" && bankHolder && (
                <div style={{ background:D.bg, border:`1px solid ${D.border2}`, padding:"13px 15px", display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                  <Building2 size={14} color={D.offwhite}/>
                  <div>
                    <div style={{ fontSize:11, color:D.offwhite, fontWeight:500 }}>{bankHolder || "Account holder"}</div>
                    {bankIBAN && <div style={{ fontSize:9, color:D.muted, marginTop:2, fontFamily:"'Courier New',monospace" }}>{bankIBAN.slice(0,12).toUpperCase()}…</div>}
                    <div style={{ fontSize:9, color:D.muted, marginTop:2 }}>{bankType.toUpperCase()} · {bankType==="sepa"?"1–2 days":"3–5 days"}</div>
                  </div>
                </div>
              )}

              {method==="cosmoola" && cSelected && (
                <div style={{ background:D.bg, border:`1px solid ${D.border2}`, padding:"13px 15px", display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
                  <Avatar user={cSelected} size={32}/>
                  <div>
                    <div style={{ fontSize:11, color:D.offwhite, fontWeight:500 }}>{cSelected.displayName}</div>
                    {cSelected.username && <div style={{ fontSize:9, color:D.muted, marginTop:2 }}>@{cSelected.username} · Instant</div>}
                  </div>
                </div>
              )}

              {/* TRANSFER BUTTON */}
              <motion.button
                onClick={handleTransfer}
                disabled={!canTransfer}
                whileTap={canTransfer ? ({ scale:0.97 } as any) : undefined}
                style={{
                  width:"100%", padding:"16px 0",
                  background: canTransfer ? D.bg : L.border,
                  color:      canTransfer ? D.white : L.muted,
                  border:"none",
                  cursor:  canTransfer ? "pointer" : "not-allowed",
                  fontSize:12, fontWeight:600, letterSpacing:"0.2em",
                  fontFamily:"'Montserrat',sans-serif",
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  opacity: loading ? 0.7 : 1,
                  transition:"background 0.2s, color 0.2s",
                }}
              >
                {loading
                  ? <><Loader2 size={15} className="tr-spin"/> PROCESSING...</>
                  : canTransfer
                    ? <><ArrowRight size={15}/> {method==="cosmoola"?"SEND NOW":"TRANSFER NOW"}</>
                    : method==="card" && savedCards.length===0   ? "ADD A CARD FIRST"
                    : method==="card" && !selectedCardId          ? "SELECT A CARD"
                    : method==="mobile" && mPhone.length<6        ? "ENTER PHONE NUMBER"
                    : method==="bank" && !bankHolder.trim()       ? "ENTER BANK DETAILS"
                    : method==="bank" && !bankIBAN.trim()         ? "ENTER IBAN"
                    : method==="cosmoola" && !cSelected           ? "SEARCH A USER"
                    : insufficient                                ? "INSUFFICIENT BALANCE"
                    : method==="bank" && bankType==="swift" && amount<100 ? "MIN €100 FOR SWIFT"
                    : "TRANSFER NOW"
                }
              </motion.button>

              <p style={{ fontSize:10, color:L.muted, textAlign:"center", marginTop:12, lineHeight:1.7 }}>
                {method==="card"     && "Card transfers: 1–3 business days"}
                {method==="mobile"   && "Mobile transfers: within 1–24 hours"}
                {method==="bank"     && bankType==="sepa"  && "SEPA: 1–2 business days · Free at most EU banks"}
                {method==="bank"     && bankType==="swift" && "SWIFT: 3–5 business days · Worldwide"}
                {method==="cosmoola" && "Instant · Zero fee · Wallet to wallet"}
              </p>

              <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6, marginTop:16, paddingTop:16, borderTop:`1px solid ${L.border}` }}>
                <Shield size={12} color={L.muted}/>
                <span style={{ fontSize:9, color:L.muted, letterSpacing:"0.1em" }}>256-BIT ENCRYPTED · SECURE TRANSFER</span>
              </div>
            </>)}

            {(step==="success"||step==="failed") && (
              <div style={{ textAlign:"center" }}>
                <p style={{ fontSize:10, color:L.textSoft, letterSpacing:"0.2em", marginBottom:16 }}>WALLET BALANCE</p>
                <p style={{ fontSize:28, fontWeight:300, color:L.text, fontFamily:"'Cormorant Garamond',serif" }}>{fmt(balance)}</p>
                <p style={{ fontSize:10, color:L.muted, marginTop:8 }}>Updated in real time</p>
                <button onClick={() => navigate(`/transactions/${userId}`)}
                  style={{ width:"100%", marginTop:20, padding:"13px 0", background:D.bg, border:"none", color:D.white, cursor:"pointer", fontSize:11, fontWeight:500, letterSpacing:"0.14em", fontFamily:"'Montserrat',sans-serif", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                  <ChevronRight size={13}/> VIEW TRANSACTIONS
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      <footer style={{ background:D.bg, borderTop:`1px solid ${D.border}`, padding:"28px 24px", marginTop:40 }}>
        <div style={{ maxWidth:1200, margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:12 }}>
          <div style={{ fontSize:11, color:D.muted }}>© 2025–{new Date().getFullYear()} Cosmoola Technologies</div>
          <div style={{ display:"flex", gap:20 }}>
            {["privacy","terms","support"].map(p => (
              <span key={p} onClick={() => navigate(`/${p}`)} style={{ fontSize:11, color:D.muted, cursor:"pointer", textDecoration:"underline", textUnderlineOffset:3 }}>
                {p.charAt(0).toUpperCase()+p.slice(1)}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Transfer;

const TR_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#fff}button{font-family:inherit}input{font-family:inherit}
  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}
  input[type=number]{-moz-appearance:textfield}
  ::-webkit-scrollbar{width:0;height:0}

  @keyframes tr-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
  .tr-spin{animation:tr-spin 1s linear infinite}

  .tr-container{
    max-width:1200px; margin:0 auto;
    padding:32px 24px 0;
    display:grid;
    grid-template-columns:1fr 380px;
    gap:28px;
    align-items:start;
  }
  .tr-main{min-width:0}
  .tr-summary{min-width:0}
  .tr-back-label{display:inline}

  @media(max-width:900px){
    .tr-container{grid-template-columns:1fr!important;gap:0!important;padding:20px 16px 0!important}
    .tr-summary{position:fixed!important;bottom:0;left:0;right:0;z-index:90;padding:0!important}
    .tr-summary > div{border:none!important;border-top:1px solid #e5e5e5!important;position:relative!important;top:0!important;box-shadow:0 -4px 20px rgba(0,0,0,0.08)}
    .tr-main{padding-bottom:240px}
    .tr-back-label{display:none}
  }
`;